import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Pause, Play, SkipForward, SkipBack, Volume2, Mic, MicOff } from 'lucide-react';
import { api } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { stopGlobalVoice } from './VoiceReader';

/**
 * Cook Mode (hands-free) — Premium feature
 *
 * Flow per step:
 *   1. fetch TTS audio for the step in the current language
 *   2. play audio
 *   3. when audio ends: if step.timer > 0 → start countdown
 *                       else → wait 3s, then advance
 *   4. when countdown reaches 0 → advance to the next step
 *   5. at the last step: announce "done" toast and exit
 *
 * Voice commands (multi-language): "next" / "previous" / "stop" via Web Speech API.
 * Manual buttons mirror those.
 */
export default function CookMode({ recipe, onClose }) {
  const { t, lang, isRTL } = useLang();
  const navigate = useNavigate();
  const steps = useMemo(
    () => (lang === 'ar' ? recipe.steps_ar : (recipe[`steps_${lang}`] || recipe.steps_en)) || [],
    [lang, recipe]
  );
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [phase, setPhase] = useState('idle'); // idle | speaking | timer | gap
  const [remaining, setRemaining] = useState(0);
  const [listening, setListening] = useState(false);

  const audioRef = useRef(null);
  const intervalRef = useRef(null);
  const gapTimeoutRef = useRef(null);
  const recRef = useRef(null);
  const idxRef = useRef(0);

  // Keep idxRef in sync (so closures inside speech recognition see the latest)
  useEffect(() => { idxRef.current = idx; }, [idx]);

  // --- Helpers ----------------------------------------------------------
  const stopAll = useCallback(() => {
    if (audioRef.current) { try { audioRef.current.pause(); } catch { /* noop */ } audioRef.current = null; }
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    if (gapTimeoutRef.current) { clearTimeout(gapTimeoutRef.current); gapTimeoutRef.current = null; }
  }, []);

  const advance = useCallback(() => {
    stopAll();
    setIdx((i) => {
      const next = i + 1;
      if (next >= steps.length) {
        toast.success(t('cookDone'));
        setTimeout(() => onClose?.(), 1200);
        return i;
      }
      return next;
    });
  }, [steps.length, stopAll, t, onClose]);

  const goBack = useCallback(() => {
    stopAll();
    setIdx((i) => Math.max(0, i - 1));
  }, [stopAll]);

  // --- Speak + chain into timer/auto-advance ----------------------------
  const speakCurrentStep = useCallback(async (stepIndex) => {
    setPhase('speaking');
    let audioB64;
    try {
      const { data } = await api.post(`/recipes/${recipe.recipe_id}/speak`, {
        language: lang,
        step_index: stepIndex,
        voice: lang === 'ar' ? 'shimmer' : 'nova',
        model: 'tts-1',
      });
      audioB64 = data.audio_b64;
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error(t('needPremium'));
        onClose?.();
        navigate('/premium');
        return;
      }
      // TTS failed — fall through to timer/gap anyway
      console.warn('[CookMode] speak failed', e);
    }

    const startNextPhase = () => {
      const step = steps[stepIndex] || {};
      if (step.timer && step.timer > 0) {
        setPhase('timer');
        setRemaining(step.timer);
      } else {
        setPhase('gap');
        gapTimeoutRef.current = setTimeout(() => advance(), 3000);
      }
    };

    if (!audioB64) { startNextPhase(); return; }

    const audio = new Audio(`data:audio/mp3;base64,${audioB64}`);
    audioRef.current = audio;
    audio.onended = startNextPhase;
    audio.onerror = startNextPhase;
    try {
      await audio.play();
    } catch (err) {
      // autoplay blocked — show a hint and call startNextPhase manually
      console.warn('[CookMode] autoplay blocked', err);
      startNextPhase();
    }
  }, [recipe.recipe_id, lang, steps, advance, t, onClose, navigate]);

  // --- Countdown driver (only ticks while phase === 'timer' && !paused)
  useEffect(() => {
    if (phase !== 'timer' || paused) return;
    intervalRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          try { new Audio('data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQAAAAA=').play(); } catch { /* noop */ }
          advance();
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => { if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; } };
  }, [phase, paused, advance]);

  // --- Drive the flow: when idx changes (and not paused), speak again ---
  useEffect(() => {
    if (paused) return;
    // Make sure any active "listen to whole recipe" audio is stopped
    // before Cook Mode takes over the audio output.
    stopGlobalVoice();
    stopAll();
    speakCurrentStep(idx);
    return stopAll;
  }, [idx, paused, speakCurrentStep, stopAll]);

  // --- Pause/Resume toggle ---------------------------------------------
  const togglePause = () => {
    setPaused((p) => {
      const next = !p;
      if (next) {
        if (audioRef.current) { try { audioRef.current.pause(); } catch { /* noop */ } }
        if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
        if (gapTimeoutRef.current) { clearTimeout(gapTimeoutRef.current); gapTimeoutRef.current = null; }
      } else {
        // resume: just kick the phase machine — if we were speaking, replay the audio
        if (phase === 'speaking' && audioRef.current) {
          audioRef.current.play().catch(() => {});
        } else if (phase === 'gap') {
          gapTimeoutRef.current = setTimeout(() => advance(), 1500);
        }
      }
      return next;
    });
  };

  // --- Voice commands (Web Speech API) ---------------------------------
  const toggleListening = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.info('Voice not supported'); return; }
    if (listening) { try { recRef.current?.stop(); } catch { /* noop */ } setListening(false); return; }
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = false;
    rec.lang = lang === 'ar' ? 'ar-SA' : lang === 'fr' ? 'fr-FR' : lang === 'es' ? 'es-ES'
             : lang === 'tr' ? 'tr-TR' : lang === 'ur' ? 'ur-PK' : 'en-US';
    rec.onresult = (e) => {
      const tr = Array.from(e.results).slice(-1)[0][0].transcript.toLowerCase().trim();
      const isNext = ['next', 'التالي', 'تالي', 'suivant', 'siguiente', 'sonraki', 'اگلا'].some(k => tr.includes(k));
      const isPrev = ['previous', 'back', 'السابق', 'سابق', 'précédent', 'anterior', 'önceki', 'پچھلا'].some(k => tr.includes(k));
      const isStop = ['stop', 'exit', 'اوقف', 'أوقف', 'خروج', 'arrêter', 'parar', 'durdur', 'روکیں'].some(k => tr.includes(k));
      if (isNext) advance();
      else if (isPrev) goBack();
      else if (isStop) onClose?.();
    };
    rec.onend = () => setListening(false);
    rec.start();
    recRef.current = rec;
    setListening(true);
  };

  // --- Cleanup on unmount ----------------------------------------------
  useEffect(() => () => {
    stopAll();
    try { recRef.current?.stop(); } catch { /* noop */ }
  }, [stopAll]);

  // --- ESC to exit ------------------------------------------------------
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const stepText = steps[idx]?.text || '';
  const progress = ((idx + 1) / steps.length) * 100;
  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;
  const phaseLabel = phase === 'speaking'
    ? t('phaseSpeaking')
    : phase === 'timer'
      ? t('phaseTimer')
      : phase === 'gap'
        ? t('phaseGap')
        : '';

  return (
    <div
      data-testid="cook-mode"
      className={`fixed inset-0 z-[200] bg-gradient-to-br from-[#1f1611] via-[#2c2420] to-[#3a2d22] text-white overflow-hidden ${isRTL ? 'rtl' : 'ltr'}`}
    >
      {/* close + voice toggle */}
      <header className="absolute top-0 inset-x-0 px-5 py-4 flex items-center justify-between z-20">
        <button
          type="button"
          onClick={onClose}
          data-testid="cook-exit"
          className="inline-flex items-center gap-2 rounded-full bg-white/15 active:bg-white/30 hover:bg-white/20 backdrop-blur px-5 py-3 text-base transition"
        >
          <X className="h-5 w-5 pointer-events-none" /> {t('exit')}
        </button>
        <button
          type="button"
          onClick={toggleListening}
          data-testid="cook-voice-toggle"
          className={`inline-flex items-center gap-2 rounded-full px-5 py-3 text-base transition backdrop-blur ${
            listening ? 'bg-[#C05A42] text-white pulse-ring' : 'bg-white/15 active:bg-white/30 hover:bg-white/20'
          }`}
        >
          {listening ? <Mic className="h-5 w-5 pointer-events-none" /> : <MicOff className="h-5 w-5 pointer-events-none" />}
          {t('voiceCommand')}
        </button>
      </header>

      {/* progress bar */}
      <div className="absolute top-20 inset-x-0 px-5 z-10">
        <div className="h-1 rounded-full bg-white/10 overflow-hidden">
          <div className="h-full bg-[#E29F48] transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
        <div className="mt-2 text-xs uppercase tracking-widest text-white/60 flex items-center justify-between">
          <span>{t('step')} {idx + 1} / {steps.length}</span>
          <span className="inline-flex items-center gap-1">
            <Volume2 className="h-3 w-3" /> {phaseLabel}
          </span>
        </div>
      </div>

      {/* main step text */}
      <main className="absolute inset-0 flex flex-col items-center justify-center px-6 pt-24 pb-40">
        <div className="max-w-3xl text-center space-y-6">
          {phase === 'timer' && (
            <div className="font-display text-7xl sm:text-8xl font-bold tabular-nums text-[#E29F48] drop-shadow-lg">
              {String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}
            </div>
          )}
          <p
            data-testid="cook-step-text"
            className="text-2xl sm:text-4xl font-display leading-snug font-medium text-white"
          >
            {stepText}
          </p>
          {paused && (
            <p className="text-[#E29F48] text-sm uppercase tracking-widest">⏸ {t('paused')}</p>
          )}
        </div>
      </main>

      {/* bottom controls */}
      <footer className="absolute bottom-0 inset-x-0 px-5 py-6 flex items-center justify-center gap-4">
        <button
          type="button"
          onClick={goBack}
          disabled={idx === 0}
          data-testid="cook-prev"
          className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40 transition"
          aria-label={t('prev')}
        >
          {isRTL ? <SkipForward className="h-6 w-6" /> : <SkipBack className="h-6 w-6" />}
        </button>
        <button
          type="button"
          onClick={togglePause}
          data-testid="cook-pause"
          className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-[#C05A42] hover:bg-[#A04832] shadow-2xl transition"
          aria-label={paused ? t('startTimer') : t('pauseTimer')}
        >
          {paused ? <Play className="h-9 w-9" /> : <Pause className="h-9 w-9" />}
        </button>
        <button
          type="button"
          onClick={advance}
          data-testid="cook-next"
          className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-white/10 hover:bg-white/20 transition"
          aria-label={t('next')}
        >
          {isRTL ? <SkipBack className="h-6 w-6" /> : <SkipForward className="h-6 w-6" />}
        </button>
      </footer>
    </div>
  );
}
